<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';

const props = defineProps({
    name: {
        type: String,
        required: true,
    },
    /*
     * Hides the visible label without removing it, for a caller whose surrounding copy already
     * says what the field is - the standalone spa switcher reads "Showing availability for
     * [Belfast]", where a "Where" above it is noise. The element stays in the DOM because
     * aria-labelledby points at it: dropping it would leave the control with no accessible
     * name at all.
     */
    hideLabel: {
        type: Boolean,
        default: false,
    },
    label: {
        type: String,
        required: true,
    },
    options: {
        type: Array, // [{ value, label }]
        default: () => [],
    },
    placeholder: {
        type: String,
        default: null,
    },
    modelValue: {
        type: [String, Number, null],
        default: null,
    },
    id: {
        type: String,
        default: null,
    },
    boldPlaceholder: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:modelValue', 'open']);

const fieldId = computed(() => props.id ?? 'select_field_' + props.name);
const hasPlaceholder = computed(() => !!props.placeholder);

const selectedOption = computed(() =>
    props.options.find((option) => String(option.value) === String(props.modelValue)) ?? null
);

const isOpen = ref(false);
const activeIndex = ref(0);
const fieldRef = ref(null);
const popoverRef = ref(null);
const triggerRef = ref(null);
const nativeRef = ref(null);

function openPopover() {
    isOpen.value = true;
    const selectedIndex = props.options.findIndex((option) => option === selectedOption.value);
    activeIndex.value = selectedIndex === -1 ? 0 : selectedIndex;

    // Focus the popover next tick, once it's actually in the DOM/visible.
    requestAnimationFrame(() => popoverRef.value?.focus());
    emit('open');
}

function closePopover() {
    isOpen.value = false;
}

function toggle() {
    isOpen.value ? closePopover() : openPopover();
}

// Registered once at mount rather than per open/close: adding this listener
// inside the same click that opens the popover risks it firing for that same
// click as it bubbles up to document, closing what was just opened.
function onClickOutside(event) {
    if (isOpen.value && !fieldRef.value?.contains(event.target)) {
        closePopover();
    }
}

// Capture phase, not bubble: a click that mutates its own DOM (e.g. slot content
// swapped out by a v-if reacting to the same click) can remove event.target from
// the tree before a bubble-phase listener here would run, making the containment
// check below see an already-detached node and wrongly conclude "outside". Capture
// runs before the click reaches its target, so the check sees the DOM as it was
// when the user actually clicked.
onMounted(() => document.addEventListener('click', onClickOutside, true));
onBeforeUnmount(() => document.removeEventListener('click', onClickOutside, true));

function commit(option) {
    emit('update:modelValue', option.value);
    closePopover();
    triggerRef.value?.focus();
}

function onPopoverKeydown(event) {
    switch (event.key) {
        case 'ArrowDown':
            event.preventDefault();
            activeIndex.value = Math.min(activeIndex.value + 1, props.options.length - 1);
            break;
        case 'ArrowUp':
            event.preventDefault();
            activeIndex.value = Math.max(activeIndex.value - 1, 0);
            break;
        case 'Home':
            event.preventDefault();
            activeIndex.value = 0;
            break;
        case 'End':
            event.preventDefault();
            activeIndex.value = props.options.length - 1;
            break;
        case 'Enter':
        case ' ':
            event.preventDefault();
            if (props.options[activeIndex.value]) commit(props.options[activeIndex.value]);
            break;
        case 'Escape':
            event.preventDefault();
            closePopover();
            triggerRef.value?.focus();
            break;
        case 'Tab':
            closePopover();
            break;
    }
}

function onTriggerKeydown(event) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        openPopover();
    }
}

function onLabelClick() {
    // Below the 'medium' breakpoint .select_field_custom is display:none (see
    // select_field.scss), so there's nothing to toggle - ask the browser for its
    // own native picker instead. showPicker() isn't supported everywhere; there's
    // no reliable fallback, so an unsupported browser just no-ops.
    const customIsVisible = fieldRef.value
        && window.getComputedStyle(fieldRef.value.querySelector('.select_field_custom')).display !== 'none';

    if (customIsVisible) {
        toggle();
        return;
    }

    try {
        nativeRef.value?.showPicker?.();
    } catch {
        // Not user-activated, or unsupported - nothing else to do.
    }
}

defineExpose({ close: closePopover });
</script>

<template>
    <div ref="fieldRef" class="select_field enhanced" :class="{ open: isOpen }">
        <span
            class="label"
            :class="{ visually_hidden: hideLabel }"
            :id="`${fieldId}_label`"
            @click="onLabelClick"
        >{{ label }}</span>

        <select
            ref="nativeRef"
            :id="fieldId"
            :name="name"
            class="select_field_native"
            :aria-labelledby="`${fieldId}_label`"
            :value="modelValue"
            @change="emit('update:modelValue', $event.target.value)"
        >
            <option v-if="hasPlaceholder" value="" disabled hidden>{{ placeholder }}</option>
            <!-- The native list's equivalent of #before-options, which only ever reaches the
                 custom popover. Below 'medium' that popover is display:none and this <select>
                 IS the control, so anything a caller wants at the head of the list on a phone
                 has to come through here. Used by LocationSelect for its Nearby option; empty
                 everywhere else, so every other call site is unchanged.

                 A <slot> inside a <select> is safe: this is an SFC, parsed at build time by
                 Vue's own compiler rather than by the browser's HTML parser, so the content
                 model that would normally discard non-<option> children never applies. -->
            <slot name="before-native-options" />
            <option v-for="option in options" :key="option.value" :value="option.value">
                {{ option.label }}
            </option>
        </select>

        <div class="select_field_custom">
            <button
                ref="triggerRef"
                type="button"
                class="select_field_trigger plain"
                aria-haspopup="listbox"
                :aria-expanded="isOpen ? 'true' : 'false'"
                :aria-labelledby="`${fieldId}_label ${fieldId}_value`"
                @click="toggle"
                @keydown="onTriggerKeydown"
            >
                <span
                    class="select_field_value"
                    :id="`${fieldId}_value`"
                    :class="{ is_placeholder: !selectedOption, bold_placeholder: boldPlaceholder }"
                    :data-placeholder="placeholder"
                >{{ selectedOption ? selectedOption.label : placeholder }}</span>
                <!-- Trailing content INSIDE the trigger, so anything a caller puts here is
                     part of the button rather than decoration beside it that looks pressable
                     and is not. Empty by default, so every existing call site is unchanged.
                     Used by the standalone spa switcher for its pencil. -->
                <slot name="trigger-suffix" />
            </button>

            <div
                ref="popoverRef"
                class="select_field_popover"
                role="listbox"
                :aria-labelledby="`${fieldId}_label`"
                tabindex="-1"
                @keydown="onPopoverKeydown"
            >
                <span class="select_field_popover_label" aria-hidden="true">{{ label }}</span>

                <slot name="before-options" :close="closePopover" />

                <div
                    v-for="(option, index) in options"
                    :key="option.value"
                    class="select_field_option"
                    :class="{ active: index === activeIndex }"
                    role="option"
                    :aria-selected="option === selectedOption ? 'true' : 'false'"
                    @click="commit(option)"
                    @mousemove="activeIndex = index"
                >
                    {{ option.label }}
                </div>
            </div>
        </div>
    </div>
</template>
